import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { TagOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Modal, Spin, message, Input, Checkbox, ConfigProvider, List } from 'antd';
import { Label, LabelEntityType, CreateLabelRequest } from '../../types/labels';
import {
	labelsOnPartsRestCrudThunksForSupplier,
	associateLabelsWithEntity,
} from '../../thunks/labels_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { RootState } from '../../reducers/root_reducer';
import { withUserType } from '../withUserType';
import { stopDefaultEvents } from '../../utils/DataAccessUtils';
import LabelDisplay from '../LabelDisplay';
import PaginatedReduxList from '../paginated_redux_list/PaginatedReduxList';
import { EmptyState } from '../empty_state/EmptyState';
import { generateRandomColor } from '../../constants/labelConstants';
import { ColorPicker } from '../LabelCreation/ColorPicker';
import { PERMISSION_NAMES, isUserAllowedToAccess } from '../../utils/AuthUtils';

type LabelSelectorOwnProps = {
	entityType: LabelEntityType;
	entityId: number;
	associatedLabelIds?: number[];
	onChange?: (selectedLabels: Label[]) => void;
	disabled?: boolean;
	onSave?: (selectedLabels: Label[]) => void;
	refreshTable?: () => void;
};

type LabelSelectorProps = LabelSelectorOwnProps & {
	labels: Label[];
	loading: boolean;
	associateLabels: (entityId: number, labelIds: number[]) => Promise<any>;
	createLabel: (label: CreateLabelRequest) => Promise<Label>;
	userType: string;
	companyConfig: any;
	currentUser: any;
};

function LabelSelector({
	entityType,
	entityId,
	onChange,
	disabled = false,
	onSave,
	refreshTable,
	labels,
	loading,
	associateLabels: associateLabelsFn,
	createLabel: createLabelFn,
	userType,
	companyConfig,
	currentUser,
	associatedLabelIds,
}: LabelSelectorProps): React.ReactElement {
	const [modalOpen, setModalOpen] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [searchFilters, setSearchFilters] = useState({});
	const [draftSelectedLabelIds, setDraftSelectedLabelIds] = useState<Set<number>>(new Set());
	const [isSaving, setIsSaving] = useState(false);
	const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
	const [newLabelColor, setNewLabelColor] = useState(generateRandomColor());
	const [isCreatingLabel, setIsCreatingLabel] = useState(false);

	const entityLabelIds = useMemo(
		() => new Set<number>(associatedLabelIds || []),
		[associatedLabelIds]
	);

	const trimmedSearchText = useMemo(() => searchText.trim(), [searchText]);

	const canModifyParts = useMemo(() => {
		return isUserAllowedToAccess(userType)(
			PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS,
			companyConfig,
			currentUser.roles
		);
	}, [userType, companyConfig, currentUser.roles]);

	const canCreateLabel = useMemo(() => {
		if (!canModifyParts) return false;
		if (trimmedSearchText.length < 3) return false;
		return !labels.some((label) => label.label.toLowerCase() === trimmedSearchText.toLowerCase());
	}, [canModifyParts, labels, trimmedSearchText]);

	useEffect(() => {
		if (!modalOpen) return;
		const timer = setTimeout(() => {
			const next = trimmedSearchText ? { search: trimmedSearchText } : {};
			setSearchFilters((prev) => {
				if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
				return next;
			});
		}, 300);
		return () => clearTimeout(timer);
	}, [trimmedSearchText, modalOpen]);

	useEffect(() => {
		if (modalOpen) {
			setDraftSelectedLabelIds(new Set(entityLabelIds));
		} else {
			setSearchText('');
			setSearchFilters({});
			setIsColorPickerOpen(false);
			setNewLabelColor(generateRandomColor());
			setIsCreatingLabel(false);
		}
	}, [modalOpen, entityLabelIds]);

	const handleToggleLabel = useCallback((label: Label) => {
		setDraftSelectedLabelIds((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(label.id)) {
				newSet.delete(label.id);
			} else {
				newSet.add(label.id);
			}
			return newSet;
		});
	}, []);

	const handleSave = useCallback(async () => {
		try {
			setIsSaving(true);
			const selectedLabelIdsArray = Array.from(draftSelectedLabelIds);
			await associateLabelsFn(entityId, selectedLabelIdsArray);
			message.success('Labels updated');
			setModalOpen(false);

			const selectedLabels = labels.filter((label) => draftSelectedLabelIds.has(label.id));
			if (onChange) onChange(selectedLabels);
			if (onSave) onSave(selectedLabels);
			if (refreshTable) refreshTable();
		} catch (error) {
			message.error('Failed to update labels');
			console.error('Error updating labels:', error);
		} finally {
			setIsSaving(false);
		}
	}, [draftSelectedLabelIds, associateLabelsFn, entityId, labels, onChange, onSave, refreshTable]);

	const handleCancel = useCallback(() => {
		setDraftSelectedLabelIds(new Set(entityLabelIds));
		setModalOpen(false);
	}, [entityLabelIds]);

	const handleCreateLabel = useCallback(async () => {
		if (!canCreateLabel || isCreatingLabel) return;
		const data: CreateLabelRequest = {
			label: trimmedSearchText,
			color: newLabelColor,
		};
		try {
			setIsCreatingLabel(true);
			const createdLabel = await createLabelFn(data);
			setDraftSelectedLabelIds((prev) => {
				const next = new Set(prev);
				next.add(createdLabel.id);
				return next;
			});
			message.success('Label created');
			setIsColorPickerOpen(false);
			setSearchText('');
			setSearchFilters({});
			setNewLabelColor(generateRandomColor());
		} catch (error) {
			message.error('Failed to create label');
			console.error('Error creating label:', error);
		} finally {
			setIsCreatingLabel(false);
		}
	}, [canCreateLabel, isCreatingLabel, trimmedSearchText, newLabelColor, createLabelFn]);

	const renderEmptyContent = useCallback(() => {
		if (canCreateLabel) {
			return (
				<div
					className=" flex cursor-pointer flex-row items-center gap-x-3 rounded border bg-white p-2 hover:bg-gray-200"
					onClick={handleCreateLabel}
				>
					<PlusOutlined size={12} className="h-3 w-3" />
					<span className="bodyregular text-character-primary">
						Create new label: <span className="font-semibold ">`{trimmedSearchText}`</span>
					</span>
					<div
						onClick={(event) => {
							stopDefaultEvents(event);
						}}
					>
						<ColorPicker
							currentColor={newLabelColor}
							onColorChange={(color) => {
								setNewLabelColor(color);
								setIsColorPickerOpen(false);
							}}
							onCancel={() => setIsColorPickerOpen(false)}
							visible={canCreateLabel && isColorPickerOpen}
							onVisibleChange={(visible) => setIsColorPickerOpen(visible)}
							buttonDisplay={
								<span
									className="inline-block h-3 w-3 rounded-full"
									style={{ backgroundColor: newLabelColor }}
								/>
							}
						/>
					</div>
				</div>
			);
		}

		const defaultMessage =
			trimmedSearchText.length >= 3 ? (
				<div className="text-center">
					<div className="h5Medium text-character-primary	">No labels found.</div>
					<div className="bodyregular mt-2 text-character-secondary">
						Check spelling or try a different search term.
					</div>
				</div>
			) : (
				<div className="text-center">
					<div className="h5Medium text-character-primary">No labels found.</div>
					<div className="bodyregular mt-2 text-character-secondary">
						Type at least 3 characters to search.
					</div>
				</div>
			);

		return (
			<div className="mt-4">
				<EmptyState body={defaultMessage} height="auto" />
			</div>
		);
	}, [canCreateLabel, handleCreateLabel, trimmedSearchText, newLabelColor, isColorPickerOpen]);

	return (
		<div onClick={stopDefaultEvents}>
			<Button
				type="link"
				size="small"
				icon={<PlusOutlined />}
				disabled={disabled}
				onClick={() => setModalOpen(true)}
			>
				Add Labels
			</Button>

			<Modal
				title={
					<div className="flex items-center gap-2">
						<TagOutlined />
						<span>Add Labels</span>
					</div>
				}
				visible={modalOpen}
				onCancel={handleCancel}
				footer={[
					<Button key="cancel" onClick={handleCancel} disabled={isSaving}>
						Cancel
					</Button>,
					<Button
						key="save"
						type="primary"
						onClick={handleSave}
						loading={isSaving || isCreatingLabel}
						disabled={isSaving}
					>
						Save
					</Button>,
				]}
				width={500}
				destroyOnClose
				closable={false}
				maskClosable={false}
			>
				<div className="">
					<Input
						placeholder="Search labels..."
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
						allowClear
					/>
				</div>

				<div className="max-h-96 overflow-y-auto [&_.ant-list-empty-text]:p-0 [&_.ant-list-items]:mt-4">
					{/* @ts-ignore - connected component typing */}
					<ConfigProvider renderEmpty={() => renderEmptyContent()}>
						{/* @ts-ignore - connected component typing */}
						<PaginatedReduxList
							collectionName="labels"
							targetCollectionName={entityType}
							listItemRenderFunction={(item: Label) => {
								const isSelected = draftSelectedLabelIds.has(item.id);
								return (
									<div
										onClick={() => handleToggleLabel(item)}
										className="flex cursor-pointer items-center gap-2 rounded p-2 transition-colors hover:bg-gray-100"
									>
										<Checkbox
											checked={isSelected}
											onChange={() => handleToggleLabel(item)}
											onClick={stopDefaultEvents}
										/>
										<LabelDisplay tag={item} />
									</div>
								);
							}}
							fetchData={labelsOnPartsRestCrudThunksForSupplier.read}
							initialPagination={{ current: 1, pageSize: 10 }}
							initialSorting={{ sort_by: 'label', order: 'asc' }}
							initialFilters={{}}
							additionalParams={modalOpen ? searchFilters : {}}
							itemLayout="horizontal"
						/>
					</ConfigProvider>
				</div>
			</Modal>
		</div>
	);
}

const mapStateToProps = (
	state: RootState,
	ownProps: LabelSelectorOwnProps & { userType: string }
) => ({
	labels: getRecordsForTargetCollection(state.labels, ownProps.entityType) || [],
	loading: state.labels[ownProps.entityType] ? state.labels[ownProps.entityType].fetching : false,
	companyConfig: state.company_config.detail,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (
	dispatch: any,
	ownProps: LabelSelectorOwnProps & { userType: string }
) => ({
	associateLabels: (entityId: number, labelIds: number[]) =>
		dispatch(associateLabelsWithEntity(ownProps.userType)(ownProps.entityType, entityId, labelIds)),
	createLabel: (label: CreateLabelRequest) =>
		dispatch(labelsOnPartsRestCrudThunksForSupplier.create(label, ownProps.entityType)),
});

// @ts-ignore - Type inference issue with HOC composition
export default withUserType(connect(mapStateToProps, mapDispatchToProps)(LabelSelector));
